import React, { useContext, useState, useEffect, useRef } from "react"
import { withTranslation } from "react-i18next"
import PropTypes from "prop-types"

import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Dropdown,
  DropdownToggle,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "reactstrap"
import { Collapse, Button as AntdButton, Select, Tooltip, Switch } from "antd"
import SimpleBar from "simplebar-react"

import { v4 as uuidv4 } from "uuid"
import GlobalState from "contexts/GlobalState"
import Lottie from "react-lottie"
import { getLottieLoadingOptions, showToast } from "utils/general"
import {
  getPageGuide,
  updatePageGuide,
  addGuideRating,
} from "helpers/backend_helper"

import ReactQuill from "react-quill"
import "react-quill/dist/quill.snow.css"

const { Panel } = Collapse
const { Option } = Select
const LANGUAGES = ["en", "fi", "sv"]

// Quill toolbar & formats
const quillModules = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
}
const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
]

const QuickGuide = props => {
  const appLang = (localStorage.getItem("i18nextLng") || "en").substring(0, 2)
  const [lang, setLang] = useState(appLang)
  const [guide, setGuide] = useState([])
  const [loadingGuide, setLoadingGuide] = useState(false)

  const [isEditing, setIsEditing] = useState(false)
  const [quillValues, setQuillValues] = useState({}) // { [sectionKey]: string (HTML) }

  const [UserState] = useContext(GlobalState)
  const [userRole, setUserRole] = useState(null)

  const [ratings, setRatings] = useState({
    positiveRatings: 0,
    negativeRatings: 0,
  })
  const [userVote, setUserVote] = useState(null) // true | false | null
  const [pending, setPending] = useState(false)
  const [clearVotes, setClearVotes] = useState(false)
  const [dirty, setDirty] = useState(false)

  const [confirmModalOpen, setConfirmModalOpen] = useState(false)

  const abortRef = useRef(null)
  const initialGuideRef = useRef(null)

  const quillTooltipStyles = `
    .quick-guide-offcanvas .ql-tooltip {
      z-index: 11000; /* hiukan Offcanvasia isompi */
    }
  `

  const visibleSections = guide.filter(section => {
    if (!section.sub_page_id) return true // näkyy kaikilla alasivuilla
    return section.sub_page_id === props.activeSubPageId
  })

  const hasSubPages = Array.isArray(props.subPages) && props.subPages.length > 0

  useEffect(() => {
    abortRef.current = new AbortController()
    if (UserState.authUser != null) {
      const obj = JSON.parse(UserState.authUser)
      let role = obj.activeRole ? obj.activeRole : obj.role
      setUserRole(role)
    }
    return () => {
      abortRef.current.abort()
    }
  }, [])

  useEffect(() => {
    // Lataa ohjeet kun paneeli avataan
    if (
      props.isOpen &&
      (guide?.length === 0 || props.hidden) &&
      !loadingGuide
    ) {
      setLoadingGuide(true)
      getPageGuide(abortRef.current, props.source)
        .then(result => {
          if (result.statusCode === 200 && result.content) {
            const parsedGuide = JSON.parse(result.content)
            setGuide(parsedGuide)
            setRatings({
              positiveRatings: JSON.parse(result.positiveRatings),
              negativeRatings: JSON.parse(result.negativeRatings),
            })
          } else {
            setGuide([])
          }
        })
        .catch(err => {
          console.log(err)
        })
        .finally(() => setLoadingGuide(false))
    }
  }, [props.isOpen])

  const toggleOpen = () => props.setIsOpen(prev => !prev)

  // Kun siirrytään muokkaustilaan, alusta editorien arvot HTML:stä
  useEffect(() => {
    if (isEditing) {
      // tallennetaan muokkaussession aloitustila vain kerran (dirty=false)
      if (!dirty) {
        initialGuideRef.current = structuredClone(guide)
      }
      const values = {}
      for (const section of guide) {
        values[section.key] = section.content?.[lang] ?? ""
      }
      setQuillValues(values)
    }
  }, [isEditing, lang]) // lang mukana, jotta kun kieli vaihtuu, arvot päivittyvät

  const handleLangChange = newLang => {
    const updatedGuide = guide.map(section => {
      const currentHtml = quillValues[section.key]
      // jos ei ole kirjoitettu mitään tähän sectioon, ei kosketa
      if (currentHtml === undefined) return section

      return {
        ...section,
        content: {
          ...(section.content || {}),
          [lang]: currentHtml, // nykyisen kielen html talteen
        },
      }
    })
    setGuide(updatedGuide)
    setLang(newLang)

    // Alustetaan quillValues uudelle kielelle
    const newValues = {}
    for (const section of updatedGuide) {
      newValues[section.key] = section.content?.[newLang] ?? ""
    }
    setQuillValues(newValues)
  }

  const onCancel = () => {
    // Peruuttaa KOKO muokkaussession muutokset
    if (initialGuideRef.current) setGuide(initialGuideRef.current)
    setQuillValues({})
    setDirty(false)
    setIsEditing(false)
  }

  const onTitleChange = (key, value) => {
    setDirty(true)
    setGuide(prev =>
      prev.map(section =>
        section.key === key
          ? {
              ...section,
              title: { ...(section.title || {}), [lang]: value },
            }
          : section
      )
    )
  }

  const onSave = () => {
    const updatedGuide = guide.map(section => {
      const html = quillValues[section.key] ?? section.content?.[lang] ?? ""
      return {
        ...section,
        content: { ...(section.content || {}), [lang]: html },
      }
    })

    console.log("Updated guide payload:", updatedGuide)
    console.log("JSON.stringify(updatedGuide):", JSON.stringify(updatedGuide))

    updatePageGuide(abortRef.current, props.source, updatedGuide, clearVotes)
      .then(result => {
        if (result.statusCode === 200 && result.content) {
          let parsed = JSON.parse(result.content)
          setGuide(parsed)
        } else {
          console.log("Update failed")
          showToast(props.t("Error"), "error", 3000, "toast-bottom-right")
        }

        if (clearVotes) {
          setRatings({
            positiveRatings: 0,
            negativeRatings: 0,
          })
        }
      })
      .catch(err => {
        console.log(err)
      })

    setIsEditing(false)
    setDirty(false)
    initialGuideRef.current = null
  }

  const onDeleteSection = key => {
    if (!globalThis.confirm(props.t("Are you sure you want to delete"))) return
    setGuide(prev => prev.filter(s => s.key !== key))
    setQuillValues(prev => {
      const { [key]: __, ...rest } = prev
      return rest
    })
    setDirty(true)
  }

  const onAddSection = () => {
    const newKey = uuidv4()
    const newSection = {
      key: newKey,
      sub_page_id: props.activeSubPageId || null, // oletuksena nykyinen alasivu
      title: { en: "", fi: "", sv: "" },
      content: { en: "", fi: "", sv: "" }, // HTML-stringit
    }
    setGuide(prev => [...prev, newSection])
    setQuillValues(prev => ({
      ...prev,
      [newKey]: "",
    }))
    setDirty(true)
  }

  const indexByKey = key => guide.findIndex(s => s.key === key)

  const moveSection = (key, delta) => {
    setGuide(prev => {
      const from = indexByKey(key)
      if (from === -1) return prev
      const to = from + delta
      if (to < 0 || to >= prev.length) return prev
      const copy = [...prev]
      const [item] = copy.splice(from, 1)
      copy.splice(to, 0, item)
      return copy
    })
    setDirty(true)
  }

  const handleFeedBack = isPositive => {
    if (pending) return
    setPending(true)
    addGuideRating(abortRef.current, props.source, isPositive)
      .then(result => {
        if (result.statusCode === 200) {
          setUserVote(isPositive)
        } else {
          console.log("Rating failed")
        }
      })
      .catch(err => {
        console.error("Rating error:", err)
      })
      .finally(() => setPending(false))

    const message = props.t("thankYouForFeedback")
    showToast(message, "success", 5000, "toast-bottom-right")
  }

  // Edit-painikkeen toiminta
  const handleEditClick = () => {
    if (isEditing) {
      const updatedGuide = guide.map(section => {
        const html = quillValues[section.key] ?? section.content?.[lang] ?? ""
        return {
          ...section,
          content: { ...(section.content || {}), [lang]: html },
        }
      })
      setGuide(updatedGuide)
      setIsEditing(false)
      // dirty pysyy true, kunnes tallennetaan tai perutaan
    } else {
      setIsEditing(true)
    }
  }

  return (
    <span className="mb-0">
      {!props.hidden && (
        <Dropdown toggle={toggleOpen}>
          <Tooltip title={props.t("Quick guide")} trigger={["hover", "focus"]}>
            <DropdownToggle
              aria-haspopup="menu"
              aria-label={props.t("Quick guide")}
              tag="button"
              id="info-button"
              className="btn focus-grey d-inline-flex"
              style={{ padding: "6px 8px" }}
            >
              <i className="bx bx-book-content font-size-16">
                {" "}
                {props.t("Quick guide")}
              </i>
            </DropdownToggle>
          </Tooltip>
        </Dropdown>
      )}

      <Offcanvas
        zIndex={10}
        isOpen={props.isOpen}
        direction="end"
        fade={false}
        className="quick-guide-offcanvas"
        backdrop={false}
        toggle={toggleOpen}
      >
        <OffcanvasHeader toggle={toggleOpen}>
          <div className="d-flex align-items-center">
            <i className="bx bx-book-content me-2" /> {props.t("Quick guide")}
            {userRole === "system_admin" && (
              <>
                <div className="d-flex align-items-center justify-content-start">
                  <Tooltip
                    title={props.t("Edit")}
                    trigger={["hover", "focus"]}
                    className="ms-3"
                  >
                    <Button
                      color="primary"
                      style={{
                        height: 28,
                        paddingTop: "0px",
                        paddingBottom: "0px",
                      }}
                      onClick={handleEditClick}
                    >
                      <i className="mdi mdi-pencil" />
                    </Button>
                  </Tooltip>

                  {isEditing || dirty ? (
                    <Button
                      color="success"
                      onClick={() => setConfirmModalOpen(true)}
                      style={{
                        height: 28,
                        paddingTop: "0px",
                        paddingBottom: "0px",
                      }}
                      className="ms-2"
                    >
                      {props.t("Save")}
                    </Button>
                  ) : null}

                  <Modal
                    isOpen={confirmModalOpen}
                    centered
                    toggle={() => setConfirmModalOpen(false)}
                  >
                    <ModalHeader toggle={() => setConfirmModalOpen(false)}>
                      {props.t("Save changes")}
                    </ModalHeader>
                    <ModalBody>
                      <p>Haluatko varmasti julkaista muutokset?</p>
                      <p className="d-flex align-items-center">
                        Nollataanko ohjeen arvostelut?
                        <Switch
                          className="ms-2"
                          checkedChildren={props.t("Yes")}
                          unCheckedChildren={props.t("No")}
                          checked={clearVotes}
                          onChange={setClearVotes}
                        />
                      </p>
                    </ModalBody>
                    <ModalFooter>
                      <Button
                        color="primary"
                        outline
                        onClick={() => setConfirmModalOpen(false)}
                      >
                        {props.t("Cancel")}
                      </Button>
                      <Button
                        color="success"
                        onClick={() => {
                          onSave()
                          setConfirmModalOpen(false)
                        }}
                      >
                        {props.t("Save")}
                      </Button>
                    </ModalFooter>
                  </Modal>
                </div>
              </>
            )}
          </div>
        </OffcanvasHeader>

        <SimpleBar
          style={{
            height: "100%",
            maxHeight: "100%",
            overflowY: "auto",
          }}
        >
          <OffcanvasBody>
            {loadingGuide ? (
              <Lottie
                options={getLottieLoadingOptions()}
                height={100}
                width={100}
              />
            ) : (
              <div className="instructions">
                {isEditing && userRole === "system_admin" && (
                  <div
                    className="mb-2 p-2"
                    style={{
                      border: "1px solid #ddd",
                      borderRadius: 4,
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-end ps-2 mb-2">
                      <span className="fw-bold me-2 ">
                        Valitse muokattava kieli:
                      </span>
                      <Select
                        value={lang}
                        onChange={handleLangChange}
                        style={{ width: 60 }}
                      >
                        {LANGUAGES.map(l => (
                          <Option key={l} value={l}>
                            {l.toUpperCase()}
                          </Option>
                        ))}
                      </Select>
                    </div>
                    <div className="d-flex align-items-center">
                      <Button
                        color="primary"
                        className="compact-button"
                        outline
                        onClick={onCancel}
                        style={{ width: 130 }}
                      >
                        {props.t("Cancel")}
                      </Button>
                      <Button
                        color="success"
                        className="ms-auto compact-button"
                        onClick={onAddSection}
                        style={{ width: 130 }}
                      >
                        {props.t("AddSection")}
                      </Button>
                    </div>
                  </div>
                )}

                {!isEditing && (
                  <h5 className="font-size-15 px-1">
                    {props.t(props.title) ?? "Undefined"}
                  </h5>
                )}

                {isEditing
                  ? guide.map((section, idx) => (
                      <div
                        key={section.key}
                        style={{
                          border: "1px solid #ddd",
                          padding: 2,
                          marginBottom: 12,
                          borderRadius: 4,
                          backgroundColor: "lightgray",
                        }}
                      >
                        <Input
                          placeholder={props.t("leaveBlankIfNoSection")}
                          value={section.title[lang] || ""}
                          onChange={e =>
                            onTitleChange(section.key, e.target.value)
                          }
                          style={{ marginBottom: 3 }}
                        />

                        <div
                          style={{
                            border: "1px solid #ccc",
                            padding: 8,
                            borderRadius: 4,
                            backgroundColor: "white",
                          }}
                        >
                          {/* React-Quill editor (HTML) */}
                          <style>{quillTooltipStyles}</style>
                          <span className="mb-0">
                            <ReactQuill
                              theme="snow"
                              placeholder={props.t("customerInstruction")}
                              value={quillValues[section.key] ?? ""}
                              onChange={html => {
                                setDirty(true)
                                setQuillValues(prev => ({
                                  ...prev,
                                  [section.key]: html,
                                }))
                              }}
                              modules={quillModules}
                              formats={quillFormats}
                              bounds=".quick-guide-offcanvas"
                            />
                          </span>

                          {/* Osion alasivu/siirto/poisto */}
                          <div
                            style={{
                              marginTop: 8,
                              display: "flex",
                              gap: 4,
                              alignItems: "center",
                            }}
                          >
                            {hasSubPages && (
                              <>
                                <span>{props.t("Subpage")} :</span>
                                <Select
                                  size="small"
                                  style={{ width: 150, marginLeft: 2 }}
                                  value={section.sub_page_id ?? "all"}
                                  onChange={value => {
                                    setDirty(true)
                                    setGuide(prev =>
                                      prev.map(s =>
                                        s.key === section.key
                                          ? {
                                              ...s,
                                              sub_page_id:
                                                value === "all" ? null : value,
                                            }
                                          : s
                                      )
                                    )
                                  }}
                                >
                                  <Option value="all">
                                    {props.t("Visible to all")}
                                  </Option>
                                  {props.subPages?.map(sp => (
                                    <Option key={sp.id} value={sp.id}>
                                      {props.t(sp.name)}
                                    </Option>
                                  ))}
                                </Select>
                              </>
                            )}
                            <div
                              style={{
                                marginLeft: "auto",
                                display: "flex",
                                gap: 4,
                                justifyContent: "flex-end",
                              }}
                            >
                              <AntdButton
                                size="small"
                                onClick={() => moveSection(section.key, -1)}
                                disabled={idx === 0}
                              >
                                <i className="mdi mdi-arrow-up font-size-18 text-primary" />
                              </AntdButton>
                              <AntdButton
                                size="small"
                                onClick={() => moveSection(section.key, +1)}
                                disabled={idx === guide.length - 1}
                              >
                                <i className="mdi mdi-arrow-down font-size-18 text-primary" />
                              </AntdButton>
                              <AntdButton
                                size="small"
                                onClick={() => onDeleteSection(section.key)}
                              >
                                <i className="mdi mdi-trash-can font-size-18 text-danger" />
                              </AntdButton>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  : visibleSections.map(section =>
                      (section.title?.[appLang] || "").trim() ? (
                        <Collapse
                          key={section.key}
                          bordered={false}
                          className="px-0 mb-3"
                          accordion
                          style={{ backgroundColor: "transparent" }}
                        >
                          <Panel
                            header={section.title[appLang]}
                            key={section.key}
                          >
                            <div
                              className="quick-guide-content"
                              dangerouslySetInnerHTML={{
                                __html: section.content?.[appLang] ?? "",
                              }}
                            />
                          </Panel>
                        </Collapse>
                      ) : (
                        <div key={section.key} className="pt-2 px-1 mb-3">
                          <div
                            className="quick-guide-content"
                            dangerouslySetInnerHTML={{
                              __html: section.content?.[appLang] ?? "",
                            }}
                          />
                        </div>
                      )
                    )}
              </div>
            )}

            <div
              className="d-flex align-items-center"
              style={{ borderTop: "1px solid lightgrey", paddingTop: "12px" }}
            >
              {userRole !== "system_admin" ? (
                <>
                  <p style={{ fontSize: "15px", margin: 0 }}>
                    {props.t("wasInstructionHelpful")}
                  </p>
                  <Button
                    color="success"
                    outline={userVote != true}
                    className="ms-3"
                    onClick={() => handleFeedBack(true)}
                    disabled={pending || userVote != null}
                    style={{ height: "22px", padding: "1px" }}
                  >
                    <i className="bx bx-like font-size-18" />
                  </Button>
                  <Button
                    color="danger"
                    outline={userVote != false}
                    className="ms-2"
                    onClick={() => handleFeedBack(false)}
                    disabled={pending || userVote != null}
                    style={{ height: "22px", padding: "1px" }}
                  >
                    <i className="bx bx-dislike font-size-18" />
                  </Button>
                </>
              ) : (
                <>
                  <div className="d-flex align-items-center mss-3">
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        paddingRight: 8,
                        border: "1px solid grey",
                        borderRadius: "4px",
                      }}
                    >
                      <i
                        className="bx bx-like font-size-18"
                        style={{
                          size: "small",
                          backgroundColor: "#34c38f",
                          color: "white",
                          padding: "2px",
                          marginRight: 8,
                          lineHeight: 1,
                          fontSize: 20,
                        }}
                      />
                      {ratings.positiveRatings}
                    </div>
                    <div
                      className="ms-2"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        paddingRight: 8,
                        border: "1px solid grey",
                        borderRadius: "4px",
                      }}
                    >
                      <i
                        className="bx bx-dislike font-size-18"
                        style={{
                          backgroundColor: "#f46a6a",
                          color: "white",
                          padding: "2px",
                          marginRight: 8,
                          lineHeight: 1,
                          fontSize: 20,
                        }}
                      />
                      {ratings.negativeRatings}
                    </div>
                  </div>
                </>
              )}
            </div>
          </OffcanvasBody>
        </SimpleBar>
      </Offcanvas>
    </span>
  )
}

QuickGuide.propTypes = {
  t: PropTypes.any,
  source: PropTypes.string.isRequired,
  title: PropTypes.string,
  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func,
  hidden: PropTypes.bool,
  activeSubPageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  subPages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
}

export default withTranslation()(QuickGuide)
